import i18n from '@/utils/i18n';
import axios from 'axios';
import { Message, MessageBox } from 'element-ui';
import store from '@/store';
import configUrl from '../config.js';
import { getToken, appIdKey } from '@/utils/auth';
import { RequestException } from '@/utils/exception';
// create an axios instance
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API, // url = base url + request url
  // withCredentials: true, // send cookies when cross-domain requests
  timeout: 60000, // request timeout
});
const requestStack = {}; // 缓存请求队列
let debounce = null; // 防抖message提示
// request interceptor
service.interceptors.request.use(
  (config) => {
    if (config.url.includes('/auth-api/v1/')) {
      config.baseURL = '';
    }
    if (config.url.startsWith('http') || config.url.startsWith('/mock')) {
      config.baseURL = '';
    }
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    config.cancelToken = source.token;
    const repeatRequestMessage = `${i18n.t('utilsRequest.doNotRepeatRequests')}:${config.baseURL || ''}${config.url}`;
    if (config.trail) {
      const stackTarget = requestStack[`${config.baseURL}${config.url}`];
      if (stackTarget) {
        stackTarget.cancel(repeatRequestMessage);
      }
      requestStack[`${config.baseURL}${config.url}`] = source;
    } else if (!config.notCheckRepeat && config.method.toLowerCase() !== 'get') {
      // 不过滤GET请求和notCheckRepeat标记为true的请求
      if (requestStack.hasOwnProperty(`${config.baseURL}${config.url}`)) {
        source.cancel(repeatRequestMessage);
      } else {
        requestStack[`${config.baseURL}${config.url}`] = source;
      }
    }

    if (window.location.href.indexOf('from=byh') > -1) {
      config.headers['reqTag'] = 'BYH';
    } else {
      config.headers['reqTag'] = 'QZ';
    }
    const token = getToken('key');
    if (token) {
      config.headers['token'] = token;
    }

    //设置接口返回的语言
    const qzLanguage = store.state.app.language ? store.state.app.language : 'zh-CN';
    config.headers['qz-language'] = qzLanguage;

    const appId = configUrl.appId || getToken(appIdKey);
    if (appId) {
      config.headers['appId'] = appId;
    } else {
      config.headers['appId'] = 461;
    }
    config.headers['hotelCode'] = store.state.user.hotelCode;
    if (config.params === undefined) {
      // 全局增加hotelCode
      config.params = {
        hotelCode: store.state.user.hotelCode.toString(),
      };
    } else {
      if (!config.params.hotelCode) {
        config.params.hotelCode = store.state.user.hotelCode;
      }
    }
    if (config.data === undefined) {
      config.data = {
        hotelCode: store.state.user.hotelCode,
      };
    } else {
      config.data.hotelCode = store.state.user.hotelCode;
    }
    let arr = [
      '/auth-api/v1/user/settlement/save',
      '/auth-api/v1/user/settlement/update',
      '/auth-api/v1/auth/unifyLogin',
      'auth-api/v1/auth/toApp',
      '/auth-api/v1/users/checkVerificationCode',
      '/auth-api/v1/users/sendMsgByPhone',
      '/auth-api/v1/users/updateByPhoneVerificationCode',
      '/auth-api/v1/users/checkVerificationCodePhone',
      '/auth-api/v1/auth/qrcodeCheck',
      '/auth-api/v1/auth/bindWx',
    ];
    if (arr.indexOf(config.url) > -1) {
      config.data.hotelCode = undefined;
    }
    //  财务接口统一添加班次
    if (config.url.includes('fmis')) {
      const userClass = store.getters.userShifts || {};
      if (userClass) {
        config.data.classes =
          config.data.classes === null ? null : config.data.classes || userClass.name;
        if (config.method.toLowerCase() !== 'post') {
          config.params.classes =
            config.data.classes === null ? null : config.data.classes || userClass.name;
        }
      } else {
        config.data.classes = '';
        config.params.classes = '';
      }
    }
    // 打印的规格参数要追加在url后面
    if (config.printType) {
      config.params = config.params || {};
      config.params['busiTypeCode'] = config.printType;
      config.params.hotelCode = store.state.user.hotelCode;
    }
    return config;
  },
  (error) => {
    // do something with request error
    return Promise.reject(new RequestException(error));
  }
);

// response interceptor
service.interceptors.response.use(
  /**
   * If you want to get http information such as headers or status
   * Please return  response => response
   */

  /**
   * Determine the request status by custom code
   * Here is just an example
   * You can also judge the status by HTTP Status Code
   */
  async (response) => {
    if (requestStack.hasOwnProperty(response.config.url)) {
      if (response.config.trail) {
        delete requestStack[response.config.url];
      } else {
        setTimeout(() => {
          delete requestStack[response.config.url];
        }, 500);
      }
    }
    const res = response.data;
    if (response.config.url.includes('/night/trial')) {
      return res;
    }

    if (
      [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/octet-stream',
      ].includes(res.type) ||
      (response.headers['content-type'] &&
        [
          'application/vnd.ms-excel',
          'application/octet-stream',
          'text/plain',
          'application/x-msdownload',
        ].some((item) => response.headers['content-type'].includes(item)))
    ) {
      return res;
    }
    // if the custom code is not 20000, it is judged as an error.
    if (judgeIsSuccessByCode(response)) {
      // 判断是否请求成功
      if (res.code === '22000001' && store.getters.userShifts) {
        Message({
          message: i18n.t('utilsRequest.noFlightFoundPleaseSelectAgain'),
          type: 'error',
          duration: 2 * 1000,
        });
        store.commit('user/SET_SHIFTS', {
          data: null,
          isSetLocalStorage: true,
        });
      }
      return res;
    } else if (['22000001', '10420005'].includes(res.code)) {
      // 如果接口返回班次无效，则前端重新弹窗选择班次
      if (store.getters.userShifts) {
        Message({
          message: i18n.t('utilsRequest.noFlightFoundPleaseSelectAgain'),
          type: 'error',
          duration: 2 * 1000,
        });
        store.commit('user/SET_SHIFTS', {
          data: null,
          isSetLocalStorage: true,
        });
      }
    } else if (
      (res.code === 400 && res.msg === '房间名称不能重复') ||
      (res.code === 400 && res.msg === '房型名称不能重复')
    ) {
      Message({
        title: i18n.t('common.lose'),
        message: res.msg || 'Error',
        type: 'error',
        duration: 2 * 1000,
        onClose: function () {
          return Promise.reject(new RequestException(res.msg));
        },
      });
    } else {
      if (
        ![
          '/room/goods/pmscode',
          '/nightAudit/isJumpWindowRemind',
          '/system/settings/base/info',
        ].some((itemUrl) => response.config.url.includes(itemUrl)) &&
        !debounce
      ) {
        if (
          process.env.VUE_APP_ENV === 'production' &&
          res.msg &&
          res.msg.includes('server failed')
        ) {
          res.msg = i18n.t('utilsRequest.systemErrorPleaseContactTechnicalSupport');
        }
        let blobRes = null;
        if (response.request.responseType === 'blob') {
          const reader = new FileReader();
          reader.readAsText(res);
          blobRes = new Promise((resolve) => {
            reader.onload = (e) => {
              resolve(JSON.parse(e.target.result).msg);
            };
          });
        }
        const blobMsg = await blobRes;
        debounce = Message({
          message: res.msg || res.message || blobMsg || 'Error',
          type: 'error',
          duration: 2 * 1000,
          onClose() {
            debounce = null;
          },
        });
      }
      if (res.code == '401') {
        return store.dispatch('user/logout');
      }
      return Promise.reject(new RequestException(res.msg || res.ErrMsg || res.message));
    }
  },
  (error) => {
    let err = JSON.parse(JSON.stringify(error)) || {};
    if (err?.message?.includes(i18n.t('utilsRequest.doNotRepeatRequests'))) {
      const requestUrl = (err.config && err.config.url) || (error && error.config && error.config.url) || '';
      const repeatMessage = (error && error.message) || (err && err.message) || '';
      // 重复请求提示只保留给 AI 相关接口，避免影响 SaaS 其它业务页面的历史体验。
      // cancel 场景下 config.url 可能缺失，因此补充从 message 中识别接口路径。
      const isAiRequest =
        requestUrl.includes('/ai/aiNotice/') ||
        requestUrl.includes('/ai/aiRevenueManager/') ||
        repeatMessage.includes('/ai/aiNotice/') ||
        repeatMessage.includes('/ai/aiRevenueManager/');
      if (isAiRequest && !debounce) {
        debounce = Message({
          message: i18n.t('utilsRequest.doNotRepeatRequests'),
          type: 'warning',
          duration: 2 * 1000,
          onClose() {
            debounce = null;
          },
        });
      }
      return Promise.reject(new Error((error && error.message) || '请勿重复请求'));;
    }

    let { message } = error;
    let isGood = false;
    if (!message.includes(i18n.t('utilsRequest.doNotRepeatRequests'))) {
      if (!debounce) {
        if (message.includes('Network Error')) {
          message = i18n.t('utilsRequest.networkException');
          isGood = false;
        } else if (message.includes('timeout')) {
          // 由于价格日历上下线接口返回慢，需要在超时的情况下刷新页面
          if (
            error.config.url.indexOf('/pms/room/goods/disable') > -1 ||
            error.config.url.indexOf('/pms/room/goods/enable') > -1
          ) {
            isGood = true;
            MessageBox.confirm(
              i18n.t('utilsRequest.requestTimedOutPleaseRefreshThePage'),
              i18n.t('common.notice'),
              {
                closeOnClickModal: false,
                confirmButtonText: i18n.t('common.ok2'),
                cancelButtonText: i18n.t('common.cancel'),
                customClass: 'confirm-class',
              }
            )
              .then(() => {
                window.location.reload();
              })
              .catch(() => {
                window.location.reload();
              });
          } else {
            message = i18n.t('utilsRequest.requestTimeout');
            isGood = false;
          }
        } else if (message.includes('Request failed with status code')) {
          message = i18n.t('utilsRequest.requestFailed');
          isGood = false;
        }
        if (!isGood) {
          debounce = Message({
            message: message,
            type: 'error',
            duration: 2 * 1000,
            onClose() {
              debounce = null;
            },
          });
        }
      }
      if (error.config && error.config.url) {
        delete requestStack[error.config.url];
      }
    }
    return Promise.reject(new RequestException(error));
  }
);
/**
 * request({
    url,
    method: 'get',
    params,
    passCodes: [10001, 10002] // 需要通过校验的code
  })
 * 可以配置passCodes属性，每个接口单独设置successCode码,防止相互影响
 */
function judgeIsSuccessByCode({ data: { code }, config: { url, passCodes } }) {
  const codeMatch = [
    '200',
    '0',
    '30001',
    '30003',
    '42012',
    '42000',
    '42007',
    '42008',
    '405',
    '40020',
    '4001',
    '4002',
    '40022',
    '4003',
    '10001',
    '5001',
    '10420002',
    '401001'
  ].includes(code + '');
  if (codeMatch) return true;
  const urlMatch = [
    '/hardware/pay/config',
    '/hardware/pay/initiate/withdrawals',
    '/account/lh',
    'payCashAccountController/addPayCashAccount',
  ].some((itemUrl) => itemUrl === url);
  if (urlMatch) return true;
  return passCodes && passCodes.includes(code);
}
export default service;
